import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'

import { useLanguages, useOnClickOutside, useSearch } from '@/hooks'
import { ILocation, ISearchLocationResponse } from '@/types'
import { useSearchLocation } from '@/apis'

import { TextInput } from '../TextInput'

import { ISearchInputProps, ISearchRowProps } from './types'

export const SearchInput: React.FC<ISearchInputProps> = ({
  name,
  label,
  value,
  error,
  required,
  onSelect,
  children,
  className,
  placeholder,
  labelClassName,
}) => {
  const divRef = useRef<HTMLDivElement>(null)
  const [labelValue, setLabelValue] = useState<string>()
  const [locations, setLocations] = useState<ISearchLocationResponse>()

  const onLocationsSuccess = useCallback((_data: ISearchLocationResponse) => {
    setLocations(_data)
  }, [])

  const [getLocations] = useSearchLocation(onLocationsSuccess)

  const onSearch = useCallback(
    (query?: string) => {
      if (!query) return
      getLocations({
        queryParams: [
          {
            keyword: query,
          },
        ],
      })
    },
    [getLocations],
  )

  const { text, onChangeText } = useSearch(200, '', onSearch)

  const _onSelect = useCallback(
    (location?: ILocation) => () => {
      setLocations(undefined)
      onChangeText('')
      onSelect({
        name: location?.name ?? '',
        code: location?.station_id ?? '',
        latitude: `${location?.gps?.latitude ?? ''}`,
        longitude: `${location?.gps?.longitude ?? ''}`,
      })
    },
    [onSelect, onChangeText],
  )

  const { cities, airports, railStations } = useMemo(() => {
    const { cities, airports, railStations } = locations || {
      cities: [],
      airports: [],
      railStations: [],
    }
    return {
      cities,
      airports,
      railStations,
    }
  }, [locations])

  const renderAddress = useCallback(
    (type: string, bool: boolean = false) =>
      // eslint-disable-next-line react/display-name
      (location: ILocation, idx: number) => {
        return (
          <SearchRow
            idx={idx}
            key={idx}
            bool={bool}
            type={type}
            location={location}
            onSelect={_onSelect}
          />
        )
      },
    [_onSelect],
  )

  useEffect(() => {
    if (!text?.length && !!value) {
      setLabelValue(value)
    }
  }, [value, text])

  useEffect(() => {
    setLabelValue(text)
  }, [text])

  useOnClickOutside(divRef, !!locations, () => setLocations(undefined))

  return (
    <div
      ref={divRef}
      className={classNames('', className, {
        error: !!error,
      })}
    >
      {!!label && (
        <label htmlFor={name}>
          {label}
          {required && <sup> *</sup>}
        </label>
      )}
      <TextInput
        id={name}
        name={name}
        autoCorrect="off"
        required={required}
        autoCapitalize="off"
        value={labelValue || ''}
        placeholder={placeholder}
        autoComplete="noAutocomplate"
        labelClassName={labelClassName}
        className="autoComplete_wrapper"
        onChange={e => {
          if (!e.target.value) {
            _onSelect(undefined)
            setLocations(undefined)
          }
          onChangeText(e.target.value)
        }}
      >
        <ul
          className="results_list"
          hidden={!cities.length && !airports.length && !railStations.length}
        >
          {airports?.map(renderAddress('airports'))}
          {railStations?.map(renderAddress('railStations', !!airports.length))}
          {cities?.map(
            renderAddress('cities', !!railStations.length || !!airports.length),
          )}
        </ul>
      </TextInput>
      {children}
    </div>
  )
}

SearchInput.displayName = 'SearchInput'

const SearchRow: React.FC<ISearchRowProps> = ({
  idx,
  bool,
  type,
  location,
  onSelect,
}) => {
  const { t } = useLanguages()

  return (
    <li
      className={classNames('result_item', {
        bordered_item: !idx && bool,
      })}
    >
      <div
        className={classNames('left_item', {
          [`icon__category--${type}`]: !idx,
        })}
      >
        {!idx ? ` ${type}` : null}
      </div>
      <div className="right_item" onClick={onSelect(location)}>
        <span>{location.name}</span>
        <div className="btn btn-purple">{t('select')}</div>
      </div>
    </li>
  )
}

SearchRow.displayName = 'SearchRow'
